import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Button,
  Input,
  Typography
} from '@material-tailwind/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faEnvelope, faLink, faPrint, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../AuthContext';
import { DEFAULT_COMPANY_INFO } from '../../../utils/companyInfo';
import DocumentPreviewScaler from '../owners/DocumentPreviewScaler';
import JobCardPrintSheet from './JobCardPrintSheet';
import { buildJobCardFilename, generateJobCardPDF, printJobCardPDF } from './jobCardPDFGenerator';
import {
  createJobCardShareLink,
  fetchPrintableJobCard,
  sendJobCardDocumentEmail
} from './jobCardDocumentService';

const JobCardDocumentPreviewDialog = ({ open, onClose, jobCardId, initialJobCard = null, initialCompanyInfo = DEFAULT_COMPANY_INFO }) => {
  const { token, user } = useAuth();
  const authToken = token || localStorage.getItem('authToken');
  const generatedBy = user?.name || user?.username || localStorage.getItem('cfms-name') || localStorage.getItem('username') || 'Fleet Management';

  const [jobCard, setJobCard] = useState(initialJobCard);
  const [companyInfo, setCompanyInfo] = useState(initialCompanyInfo);
  const [loading, setLoading] = useState(false);
  const [busyAction, setBusyAction] = useState('');
  const [status, setStatus] = useState({ type: '', text: '' });
  const [recipientEmail, setRecipientEmail] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [shareUrl, setShareUrl] = useState('');

  const filename = useMemo(() => buildJobCardFilename(jobCard), [jobCard]);

  useEffect(() => {
    if (!open || !jobCardId) {
      return;
    }

    let active = true;

    const loadDocument = async () => {
      try {
        setLoading(true);
        setStatus({ type: '', text: '' });
        setShareUrl('');
        const payload = initialJobCard ? { jobCard: initialJobCard, companyInfo: initialCompanyInfo } : await fetchPrintableJobCard(authToken, jobCardId);

        if (!active) {
          return;
        }

        setJobCard(payload.jobCard || null);
        setCompanyInfo(payload.companyInfo || DEFAULT_COMPANY_INFO);
        setRecipientEmail(payload.jobCard?.owner_email || '');
        setEmailMessage(
          payload.jobCard?.owner_name
            ? `Hello ${payload.jobCard.owner_name},\n\nPlease find attached the job card document for ${payload.jobCard.job_card_no || payload.jobCard.id}.\n\nRegards,\n${localStorage.getItem('username') || 'Fleet Management'}`
            : 'Please find attached the job card document.'
        );
      } catch (error) {
        if (active) {
          setStatus({ type: 'error', text: error.response?.data?.message || error.message || 'Failed to load job card document.' });
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadDocument();

    return () => {
      active = false;
    };
  }, [open, jobCardId, authToken, initialJobCard, initialCompanyInfo]);

  const setBusy = (action, value = true) => setBusyAction(value ? action : '');

  const refreshDocument = async () => {
    const payload = await fetchPrintableJobCard(authToken, jobCardId);
    setJobCard(payload.jobCard || null);
    setCompanyInfo(payload.companyInfo || DEFAULT_COMPANY_INFO);
    return payload;
  };

  const handleDownload = async () => {
    try {
      setBusy('download');
      setStatus({ type: '', text: '' });
      const payload = jobCard ? { jobCard, companyInfo } : await refreshDocument();
      const pdf = await generateJobCardPDF(payload.jobCard, payload.companyInfo, generatedBy);
      pdf.save(filename);
      setStatus({ type: 'success', text: 'PDF downloaded successfully.' });
    } catch (error) {
      setStatus({ type: 'error', text: error.message || 'Failed to download PDF.' });
    } finally {
      setBusy('download', false);
    }
  };

  const handlePrint = async () => {
    try {
      setBusy('print');
      setStatus({ type: '', text: '' });
      const payload = jobCard ? { jobCard, companyInfo } : await refreshDocument();
      await printJobCardPDF(payload.jobCard, payload.companyInfo, generatedBy);
      setStatus({ type: 'success', text: 'Print dialog opened.' });
    } catch (error) {
      setStatus({ type: 'error', text: error.message || 'Failed to print PDF.' });
    } finally {
      setBusy('print', false);
    }
  };

  const handleShareLink = async () => {
    try {
      setBusy('share');
      setStatus({ type: '', text: '' });
      const payload = jobCard ? { jobCard } : await refreshDocument();
      const shareResult = await createJobCardShareLink(authToken, payload.jobCard.id);
      const frontendBase = window.location.origin.replace(/\/$/, '');
      const url = `${frontendBase}/share/job-document/${encodeURIComponent(shareResult.share_token)}?download=1`;
      setShareUrl(url);

      if (navigator.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(url);
        } catch (clipboardError) {
          console.warn('Clipboard copy failed:', clipboardError);
        }
      }

      window.open(url, '_blank', 'noopener,noreferrer');
      setStatus({ type: 'success', text: 'Share link created and opened in a new tab.' });
    } catch (error) {
      setStatus({ type: 'error', text: error.message || 'Failed to create share link.' });
    } finally {
      setBusy('share', false);
    }
  };

  const handleEmail = async () => {
    if (!recipientEmail) {
      setStatus({ type: 'error', text: 'Recipient email is required.' });
      return;
    }

    try {
      setBusy('email');
      setStatus({ type: '', text: '' });
      const payload = jobCard ? { jobCard, companyInfo } : await refreshDocument();
      const pdf = await generateJobCardPDF(payload.jobCard, payload.companyInfo, generatedBy);
      const blob = pdf.output('blob');
      const formData = new FormData();
      formData.append('recipient_email', recipientEmail);
      formData.append('subject', `${payload.jobCard.owner_name || payload.jobCard.owner_code || 'Owner'} - Job Card ${payload.jobCard.job_card_no}`);
      formData.append('message', emailMessage);
      formData.append('filename', filename);
      formData.append('pdf_file', blob, filename);

      await sendJobCardDocumentEmail(authToken, payload.jobCard.id, formData);
      setStatus({ type: 'success', text: 'Email sent successfully.' });
    } catch (error) {
      setStatus({ type: 'error', text: error.response?.data?.message || error.message || 'Failed to send email.' });
    } finally {
      setBusy('email', false);
    }
  };

  const statusClass = status.type === 'success'
    ? 'bg-green-50 text-green-700 border-green-200'
    : status.type === 'error'
      ? 'bg-red-50 text-red-700 border-red-200'
      : 'bg-slate-50 text-slate-700 border-slate-200';

  return (
    <Dialog open={open} handler={onClose} size="xl">
      <DialogHeader className="flex items-center justify-between gap-4">
        <div>
          <Typography variant="h5" className="text-[#2a2e30]">
            Job Card Preview
          </Typography>
          <Typography variant="small" className="text-gray-500">
            {jobCard?.job_card_no || jobCardId || '-'} | {jobCard?.plate_no || '-'}
          </Typography>
        </div>
        <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </DialogHeader>

      <DialogBody className="max-h-[78vh] space-y-4 overflow-y-auto">
        {status.text && (
          <div className={`rounded-xl border px-4 py-3 text-sm ${statusClass}`}>
            {status.text}
          </div>
        )}

        {!jobCard && loading && (
          <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-8 text-center text-gray-500">
            Loading job card document...
          </div>
        )}

        {jobCard && (
          <div className="max-h-[68vh] overflow-auto rounded-2xl border border-gray-200 bg-white p-3 shadow-sm">
            <DocumentPreviewScaler>
              <JobCardPrintSheet jobCard={jobCard} companyInfo={companyInfo} generatedBy={generatedBy} />
            </DocumentPreviewScaler>
          </div>
        )}
      </DialogBody>

      <DialogFooter className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:justify-between">
        <div className="flex flex-wrap gap-3">
          <Button
            onClick={handleDownload}
            disabled={busyAction === 'download' || loading || !jobCard}
            className="bg-[#0f766e] text-white"
          >
            <FontAwesomeIcon icon={faDownload} className="mr-2" />
            {busyAction === 'download' ? 'Downloading...' : 'Download PDF'}
          </Button>
          <Button
            onClick={handlePrint}
            disabled={busyAction === 'print' || loading || !jobCard}
            variant="outlined"
            className="border-[#FF5733] text-[#FF5733]"
          >
            <FontAwesomeIcon icon={faPrint} className="mr-2" />
            {busyAction === 'print' ? 'Printing...' : 'Print'}
          </Button>
          <Button
            onClick={handleShareLink}
            disabled={busyAction === 'share' || loading || !jobCard}
            variant="outlined"
            className="border-slate-300 text-slate-700"
          >
            <FontAwesomeIcon icon={faLink} className="mr-2" />
            {busyAction === 'share' ? 'Creating...' : 'Share Link'}
          </Button>
        </div>

        <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
          <Input
            label="Recipient email"
            value={recipientEmail}
            onChange={(e) => setRecipientEmail(e.target.value)}
            className="min-w-[220px]"
          />
          <Button
            onClick={handleEmail}
            disabled={busyAction === 'email' || loading || !jobCard}
            className="bg-[#ff5733] text-white"
          >
            <FontAwesomeIcon icon={faEnvelope} className="mr-2" />
            {busyAction === 'email' ? 'Sending...' : 'Email PDF'}
          </Button>
        </div>

        {shareUrl && (
          <div className="w-full rounded-xl border border-slate-200 bg-slate-50 p-3 text-left text-xs text-slate-600">
            <div className="mb-1 font-semibold text-slate-700">Share URL</div>
            <div className="break-all">{shareUrl}</div>
          </div>
        )}
      </DialogFooter>
    </Dialog>
  );
};

export default JobCardDocumentPreviewDialog;
